import BugsnagPerformance from '@bugsnag/browser-performance';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { ReactRouterRoutingProvider } from '@bugsnag/react-router-performance';
import { useEffect, useState } from 'react';
import { RouteObject } from 'react-router-dom';

import { useFrontendLogger } from './useFrontendLogger';
import { defaultSettings, useSettings } from './useSettings';

export const useLogger = () => {
  const { data: settings } = useSettings();
  const { sendToLoggingProxy } = useFrontendLogger();
  const [loggerInitiated, setLoggerInitiated] = useState(!settings.isProd);
  const [performanceInitiated, setPerformanceInitiated] = useState(!settings.isProd);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('ResizeObserver')) {
        return;
      }

      sendToLoggingProxy(
        {
          severity: 'ERROR',
          message: event.message,
          stack: event.error?.stack,
          metadata: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        },
        settings.loggingProxyUrl,
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      sendToLoggingProxy(
        {
          severity: 'ERROR',
          message: `Unhandled Promise Rejection: ${event.reason}`,
          stack: event.reason?.stack,
        },
        settings.loggingProxyUrl,
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [sendToLoggingProxy]);

  const initPerformance = (routes: RouteObject[]) => {
    if (!performanceInitiated && settings.frontendMonitoringKey) {
      BugsnagPerformance.start({
        apiKey: settings.frontendMonitoringKey,
        routingProvider: new ReactRouterRoutingProvider(routes),
      });
      setPerformanceInitiated(true);
    }
  };

  const init = () => {
    if (!loggerInitiated && settings.frontendMonitoringKey) {
      Bugsnag.start({
        apiKey: settings.frontendMonitoringKey,
        plugins: [new BugsnagPluginReact()],
        onError: function (event) {
          event.addMetadata('environment', {
            env: window.location.hostname,
          });
          if (event.errors.some((err) => err.errorClass === 'Error' && err.errorMessage.includes('ResizeObserver'))) {
            return false;
          }
        },
      });
      setLoggerInitiated(true);
    }
  };

  const setUser = (user: { _id: string }) => {
    if (defaultSettings.isProd) {
      Bugsnag.setUser(user._id);
    }
  };

  const logError = (error: Error, metadata?: Record<string, any>) => {
    sendToLoggingProxy(
      {
        severity: 'ERROR',
        message: error.message,
        stack: error.stack,
        metadata,
      },
      settings.loggingProxyUrl,
    );
    if (defaultSettings.isProd) {
      Bugsnag.notify(error);
    } else {
      // eslint-disable-next-line no-console
      console.error('Error:', error);
    }
  };

  return { logError, init, initPerformance, setUser };
};
